import { useEffect, useState } from 'react';

// The build hash baked into this bundle at build time (see vite.config.ts).
// In CI/CD this is set to `0.0.2-<github-sha>`. In local builds without the
// env var set, it falls back to 'dev-build' and polling is skipped entirely.
const BAKED_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined;

// Defensive backstop for tabs that stay focused for long stretches (e.g. an
// open workflow editor session). The common "tab in background then refocus"
// case is handled by the visibilitychange listener, not this interval.
const POLL_INTERVAL_MS = 10 * 60 * 1000;

const VERSION_JSON_URL = '/version.json';

function shouldPoll(): boolean {
  if (!BAKED_VERSION) return false;
  // Skip in local builds where the version is a placeholder. Without this, the
  // hook would compare 'dev-build' against whatever version.json happens to be
  // sitting in dist/ from a previous build and toast forever in dev preview.
  if (BAKED_VERSION === 'dev-build' || BAKED_VERSION === 'unknown')
    return false;
  if (typeof window === 'undefined') return false;
  if (window.location.hostname === 'localhost') return false;
  return true;
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_JSON_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const data: { version?: string } = await res.json();
    return data.version ?? null;
  } catch {
    // Network blip, deploy mid-flight, etc. Fail silently — we'll try again
    // on the next visibility change or interval tick.
    return null;
  }
}

/**
 * Detects when a newer version of the frontend has been deployed by polling
 * `/version.json` and comparing against the version baked into the running
 * bundle. Once a mismatch is detected, `isOutdated` flips to true and stays
 * true (the polling effect tears itself down).
 *
 * Polls on:
 *   - mount (initial check)
 *   - `visibilitychange` → visible (user returned to the tab)
 *   - a 10-minute interval while the tab is visible (long-focus backstop)
 *
 * The polling fetch uses `cache: 'no-store'`; `version.json` must also be
 * served with `Cache-Control: no-cache` from the CDN (see vercel.ts) or the
 * client will compare against a stale edge-cached value forever.
 */
export function useVersionCheck(): { isOutdated: boolean } {
  const [isOutdated, setIsOutdated] = useState(false);

  useEffect(() => {
    if (!shouldPoll()) return;
    if (isOutdated) return;

    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;

      const remoteVersion = await fetchRemoteVersion();
      if (cancelled || !remoteVersion) return;
      if (remoteVersion !== BAKED_VERSION) {
        setIsOutdated(true);
      }
    };

    void check();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(interval);
    };
  }, [isOutdated]);

  return { isOutdated };
}
